import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../components/common/PageLayout';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQItemComponent: React.FC<{ item: FAQItem; index: number }> = ({ item, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left py-4 px-2 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <span className="text-lg font-medium text-gray-900 dark:text-white pr-4" itemProp="name">
          {item.question}
        </span>
        <svg
          className={`w-6 h-6 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="px-2 pb-4 text-gray-700 dark:text-gray-300" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
          <div itemProp="text">{item.answer}</div>
        </div>
      )}
    </div>
  );
};

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;
  const questions = t('pages.faq.questions', { returnObjects: true }) as FAQItem[];

  // Generate FAQPage structured data
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ | MWQQ.com - Frequently Asked Questions About Passport Photos</title>
        <meta name="description" content="Find answers to common questions about our passport photo tool. Learn about privacy, supported countries, photo formats, and how our AI processing works." />
        <meta name="keywords" content="FAQ, frequently asked questions, passport photo questions, help, support, how to use, privacy questions" />
        <link rel="canonical" href={`${baseUrl}/faq.html`} />
        <meta property="og:title" content="FAQ | MWQQ.com" />
        <meta property="og:description" content="Frequently asked questions about our passport photo tool." />
        <meta property="og:url" content={`${baseUrl}/faq.html`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <PageLayout title={t('pages.faq.title')}>
      <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
        {t('pages.faq.intro')}
      </p>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
        {questions.map((item, index) => (
          <FAQItemComponent key={index} item={item} index={index} />
        ))}
      </div>

      <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          Still have questions?
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          If you couldn't find the answer you're looking for, feel free to contact us directly.
        </p>
        <a
          href="mailto:mwqq.com@gmail.com"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Contact Us
        </a>
      </div>
      </PageLayout>
    </>
  );
};

export default FAQ;
